import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Shield, FileText, Download, Edit, RefreshCw, CheckCircle, BookOpen, Clock, XCircle, MessageSquare, Award } from 'lucide-react';

export function CurriculumStatsCards({ stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
      <Card className="imcac-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-blue-100 text-blue-600">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1B2A4A]">{stats.total_applications || 0}</p>
            <p className="text-xs text-slate-500">Applications</p>
          </div>
        </CardContent>
      </Card>
      <Card className="imcac-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-green-100 text-green-600">
            <CheckCircle className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1B2A4A]">{stats.approved || 0}</p>
            <p className="text-xs text-slate-500">Approved</p>
          </div>
        </CardContent>
      </Card>
      <Card className="imcac-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-purple-100 text-purple-600">
            <CheckCircle className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1B2A4A]">{stats.certificates || 0}</p>
            <p className="text-xs text-slate-500">Certificates</p>
          </div>
        </CardContent>
      </Card>
      <Card className={`imcac-card ${stats.expiring_soon > 0 ? 'border-amber-500' : ''}`}>
        <CardContent className="p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${stats.expiring_soon > 0 ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-600'}`}>
            <Calendar className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1B2A4A]">{stats.expiring_soon || 0}</p>
            <p className="text-xs text-slate-500">Expiring Soon</p>
          </div>
        </CardContent>
      </Card>
      <Card className="imcac-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-indigo-100 text-indigo-600">
            <Edit className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1B2A4A]">{stats.pending_updates || 0}</p>
            <p className="text-xs text-slate-500">Pending Updates</p>
          </div>
        </CardContent>
      </Card>
      <Card className="imcac-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-rose-100 text-rose-600">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-[#1B2A4A]">{stats.unread_messages || 0}</p>
            <p className="text-xs text-slate-500">Messages</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const statusStyles = {
  submitted: { bg: 'bg-blue-100 text-blue-700', label: 'Submitted' },
  in_review: { bg: 'bg-amber-100 text-amber-700', label: 'In Review' },
  under_review: { bg: 'bg-amber-100 text-amber-700', label: 'Under Review' },
  approved: { bg: 'bg-green-100 text-green-700', label: 'Approved' },
  rejected: { bg: 'bg-red-100 text-red-700', label: 'Rejected' },
  pending: { bg: 'bg-slate-100 text-slate-700', label: 'Pending' },
};

export function CurriculumOverviewTab({ validityAlerts, certificates, applications, onDownload, onShowUpdate }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Validity Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#C5A572]" /> Certificate Validity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {validityAlerts && validityAlerts.alerts?.length > 0 ? (
            <div className="space-y-3">
              {validityAlerts.alerts.slice(0, 3).map((alert, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-sm border-l-4 ${
                    alert.alert_type === 'expired' ? 'border-l-red-500 bg-red-50' :
                    alert.alert_type === 'critical' ? 'border-l-amber-500 bg-amber-50' :
                    'border-l-blue-500 bg-blue-50'
                  }`}
                >
                  <p className="font-medium text-sm text-[#1B2A4A]">{alert.curriculum_title}</p>
                  <p className="text-xs text-slate-600">{alert.message}</p>
                  <p className="text-xs text-slate-500 mt-1">{alert.action_required}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500">
              <Shield className="h-10 w-10 mx-auto mb-2 text-green-500" />
              <p className="text-sm">All certificates are valid</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button asChild className="w-full justify-start bg-[#1B2A4A] text-white rounded-sm">
            <a href="/apply/curriculum">
              <FileText className="h-4 w-4 mr-2" /> Submit New Curriculum
            </a>
          </Button>
          {certificates.length > 0 && (
            <>
              <Button 
                onClick={() => onDownload(certificates[0]?.id)}
                variant="outline" 
                className="w-full justify-start rounded-sm"
              >
                <Download className="h-4 w-4 mr-2" /> Download Certificate
              </Button>
              <Button 
                onClick={() => onShowUpdate(certificates[0]?.id)}
                variant="outline" 
                className="w-full justify-start rounded-sm"
              >
                <Edit className="h-4 w-4 mr-2" /> Submit Curriculum Update
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle className="text-base">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {applications && applications.length > 0 ? (
            <div className="space-y-3">
              {applications.slice(0, 3).map(app => {
                const style = statusStyles[app.status] || statusStyles.pending;
                return (
                  <div key={app.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                    <div>
                      <p className="font-medium text-[#1B2A4A]">{app.curriculum_title || app.applicant_name}</p>
                      <p className="text-xs text-slate-500">{app.specialty}</p>
                    </div>
                    <Badge className={`${style.bg} rounded-sm px-2 py-0.5`}>{style.label}</Badge>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <BookOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No curriculum submissions yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
